import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PaymentUploadModal = ({ invoice, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    paymentMethod: '',
    transactionId: '',
    paymentDate: new Date()?.toISOString()?.split('T')?.[0],
    notes: '',
    file: null
  });

  const [dragActive, setDragActive] = useState(false);

  const paymentMethodOptions = [
    { value: 'bank_transfer', label: 'Virement bancaire' },
    { value: 'credit_card', label: 'Carte de crédit' },
    { value: 'check', label: 'Chèque' },
    { value: 'cash', label: 'Espèces' },
    { value: 'other', label: 'Autre' }
  ];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      setFormData({ ...formData, file: e?.dataTransfer?.files?.[0] });
    }
  };

  const handleFileChange = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      setFormData({ ...formData, file: e?.target?.files?.[0] });
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(invoice?.id, formData);
  };

  const formatAmount = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    })?.format(value);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-card rounded-lg shadow-modal max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Télécharger la preuve de paiement</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Facture {invoice?.invoiceNumber} - {formatAmount(invoice?.amount)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Patient:</span>
                <p className="font-medium text-foreground mt-1">{invoice?.patientName}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Conception:</span>
                <p className="font-medium text-foreground mt-1">#{invoice?.caseId}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Montant:</span>
                <p className="font-semibold text-foreground mt-1">{formatAmount(invoice?.amount)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Date d'échéance:</span>
                <p className="font-medium text-foreground mt-1">
                  {new Date(invoice.dueDate)?.toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </div>

          <Select
            label="Méthode de paiement"
            required
            options={paymentMethodOptions}
            value={formData?.paymentMethod}
            onChange={(value) => setFormData({ ...formData, paymentMethod: value })}
          />

          <Input
            label="ID de transaction"
            type="text"
            placeholder="Entrez l'ID de transaction"
            value={formData?.transactionId}
            onChange={(e) => setFormData({ ...formData, transactionId: e?.target?.value })}
            description="Numéro de référence ou ID de transaction"
          />

          <Input
            label="Date de paiement"
            type="date"
            required
            value={formData?.paymentDate}
            onChange={(e) => setFormData({ ...formData, paymentDate: e?.target?.value })}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Preuve de paiement <span className="text-error">*</span>
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
                dragActive
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {formData?.file ? (
                <div className="flex items-center justify-center gap-3">
                  <Icon name="FileCheck" size={32} color="var(--color-success)" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-foreground">{formData?.file?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(formData?.file?.size / 1024)?.toFixed(2)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, file: null })}
                    className="p-2 hover:bg-muted rounded-lg transition-colors duration-200"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>
              ) : (
                <>
                  <Icon name="Upload" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <p className="text-sm font-medium text-foreground mb-2">
                    Glissez-déposez votre fichier ici
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    ou cliquez pour sélectionner un fichier
                  </p>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="file-upload">
                    <Button type="button" variant="outline" size="sm" as="span">
                      Sélectionner un fichier
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground mt-3">
                    PDF, JPG, PNG jusqu'à 10MB
                  </p>
                </>
              )}
            </div>
          </div>

          <Input
            label="Notes (optionnel)"
            type="text"
            placeholder="Ajouter des notes supplémentaires"
            value={formData?.notes}
            onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
            description="Informations complémentaires sur le paiement"
          />

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" fullWidth onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              iconName="Check"
              iconPosition="left"
              disabled={!formData?.paymentMethod || !formData?.file}
            >
              Confirmer le paiement
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentUploadModal;